import React, { useEffect, useState, useMemo } from 'react'
import {
  Form,
  Input,
  InputOnChangeData,
  InputProps,
  Popup,
  SemanticShorthandItem,
  StrictFormFieldProps,
} from 'semantic-ui-react'
import { inputNumberTypeRegex } from '../../regex/fieldsValidationRegex'
import { convertStringToNumber } from '../../utils/convertStringToNumber'
import { createOffsetElement } from '../../utils/offsetElement'
import './ValidationInput.css'

export interface IInputDatalist {
  value: string
}

interface StateProps {
  value?: string | number
  validatePattern?: RegExp
  invalid?: boolean
  invalidMessage?: string
  className?: string
  disabled?: boolean
  error?: boolean
  fluid?: boolean
  focus?: boolean
  inverted?: boolean
  loading?: boolean
  size?: 'mini' | 'small' | 'large' | 'big' | 'huge' | 'massive'
  tabIndex?: number | string
  transparent?: boolean
  type?: string
  placeholder?: string
  symbol?: string
  boldSymbol?: boolean
  fieldWidth?: StrictFormFieldProps['width']
  fieldWidthPercentage?: string
  width?: string
  height?: string
  paddingRight?: string | number
  paddingLeft?: string | number
  icon?: SemanticShorthandItem<InputProps>
  label?: JSX.Element | null | undefined
  labelInside?: string
  marginTop?: string
  marginBottom?: string
  zIndex?: number
  symbolPadding?: number
  valueHeight?: boolean
  iconPosition?: 'left' | undefined
  firstChild?: boolean
  symbolLeft?: boolean
  symbolMarginLeft?: string
  symbolMarginTop?: string
  datalist?: IInputDatalist[]
  listId?: string
  wholeNumber?: boolean
  maxLength?: number
  minWidth?: string
  close?: boolean
  readOnly?: boolean
  twoDecimalPlaces?: boolean
  showWarningCase?: string
  showWarningId?: string
  assignedId?: string
  focusedId?: string
  'aria-label'?: string
  'data-testid'?: string
  /** Enable auto-expand functionality - input expands based on content */
  autoExpand?: boolean
  /** Minimum width in pixels when auto-expand is enabled (default: 50) */
  autoExpandMin?: number
  /** Maximum width in pixels when auto-expand is enabled (default: 350) */
  autoExpandMax?: number
}

interface DispatchProps {
  onChange: (e: React.ChangeEvent<HTMLInputElement>, validated?: boolean, convertedValue?: number) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
  onInput?: (e: React.FormEvent<HTMLInputElement>) => void
}

type Props = StateProps & DispatchProps

// Auto-expand constants
const AUTO_EXPAND_CHAR_WIDTH = 8
const AUTO_EXPAND_CHROME_WIDTH = 50
const AUTO_EXPAND_DEFAULT_MIN = 50
const AUTO_EXPAND_DEFAULT_MAX = 350

/**
 * Calculates input width based on text length for auto-expand
 */
const calculateAutoExpandWidth = (length: number, minWidth: number, maxWidth: number): number => {
  const contentWidth = length * AUTO_EXPAND_CHAR_WIDTH
  const totalWidth = contentWidth + AUTO_EXPAND_CHROME_WIDTH
  return Math.min(maxWidth, Math.max(minWidth, totalWidth))
}

const ValidationInput = (props: Props): JSX.Element => {
  const [validatedInput, setValidatedInput] = useState<null | boolean>(null)
  const [showSymbol, setShowSymbol] = useState<boolean>(true)
  const [inputElement, setInputElement] = useState<HTMLInputElement>()
  const [isFocused, setIsFocused] = useState<boolean>(false)
  // Track the minimum width during auto-expand session (captured on focus)
  const [autoExpandMinWidth, setAutoExpandMinWidth] = useState<number>(0)

  // Auto-expand settings
  const autoExpandMin = props.autoExpandMin ?? AUTO_EXPAND_DEFAULT_MIN
  const autoExpandMax = props.autoExpandMax ?? AUTO_EXPAND_DEFAULT_MAX

  // Memoized symbol offset calculation to prevent expensive DOM operations on every render
  const symbolOffset = useMemo(() => {
    // Only calculate if symbol is visible and has value
    if (!props.symbol || !props.value || !showSymbol) {
      return '0rem'
    }
    
    // Keep existing calculation logic exactly the same
    let left = 0
    const _text_width = createOffsetElement('1.15rem', props.value.toString())
    left = (_text_width + 15) * 0.07
    
    if (props.symbolPadding) {
      left -= props.symbolPadding
    }
    
    return `${left}rem`
  }, [props.value, props.symbol, props.symbolPadding, showSymbol])

  useEffect(() => {
    const clickOutside = (event: MouseEvent) => {
      if (event.target !== inputElement && inputElement !== undefined) {
        setShowSymbol(true)
      }
    }
    window.addEventListener('click', clickOutside)
    return () => {
      window.removeEventListener('click', clickOutside)
    }
  }, [inputElement])

  const inputOnChange = (event: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
    const converted = props.type === 'number' ? convertStringToNumber(event.target.value) : undefined
    if (props.validatePattern) {
      const validated = props.validatePattern.test(data.value)
      setValidatedInput(validated)
      props.onChange(event, validated, converted)
    } else {
      if (props.twoDecimalPlaces) {
        let _convertedDecimal = event.target.value
        let _valueArr = []
        let _indicator = ''
        if (_convertedDecimal.includes('.') && _convertedDecimal.includes(',')) {
          const normalizedDecimal = _convertedDecimal.replace(',', '.')
          _indicator = _convertedDecimal.indexOf('.') > _convertedDecimal.indexOf(',') ? ',' : '.'
          _valueArr = normalizedDecimal.split('.')
        } else {
          _indicator = _convertedDecimal?.toString().includes('.') ? '.' : ','
          _valueArr = _convertedDecimal?.toString().split(_indicator) ?? []
        }
        if (_valueArr.length > 1) {
          if (_valueArr[1].length > 2) {
            const _numReg = new RegExp(/^\d+$/).test(_valueArr[1].slice(0, 2))
            _convertedDecimal = _numReg ? _valueArr[0] + _indicator + _valueArr[1].slice(0, 2) : _valueArr[0]
          }
        }
        const e: React.ChangeEvent<HTMLInputElement> = { ...event, target: { ...event.target, value: _convertedDecimal } }
        props.onChange(e, undefined, convertStringToNumber(_convertedDecimal))
      } else {
        props.onChange(event, undefined, converted)
      }
    }
  }

  const inputOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false)
    // Auto-expand: animate back to initial width, then let CSS take over
    if (props.autoExpand && autoExpandMinWidth > 0) {
      // Animate to the initial width (column width)
      event.target.style.setProperty('width', `${autoExpandMinWidth}px`, 'important')
      // After animation completes, remove inline style so CSS stretch takes over
      setTimeout(() => {
        event.target.style.removeProperty('width')
      }, 100) // Match the CSS transition duration
    }
    if (props.onBlur) {
      props.onBlur(event)
    }
  }

  const inputOnFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true)
    // Auto-expand: set width based on content, but never shrink below current width
    if (props.autoExpand) {
      const currentWidth = event.target.offsetWidth
      // Store the initial width as the minimum for this editing session
      setAutoExpandMinWidth(currentWidth)
      const length = event.target.value.length
      const contentWidth = calculateAutoExpandWidth(length, autoExpandMin, autoExpandMax)
      // Use the larger of current width or content width (never shrink)
      const targetWidth = Math.max(currentWidth, contentWidth)

      // For smooth animation: first lock current width, then animate to target
      event.target.style.setProperty('width', `${currentWidth}px`, 'important')
      // Force reflow to ensure the initial width is applied before transition
      void event.target.offsetWidth
      // Now animate to target width
      requestAnimationFrame(() => {
        event.target.style.setProperty('width', `${targetWidth}px`, 'important')
      })
    }
    if (props.onFocus) {
      props.onFocus(event)
    }
  }

  const inputOnInput = (event: React.FormEvent<HTMLInputElement>) => {
    // Auto-expand: adjust width as user types, but never shrink below initial width
    if (props.autoExpand) {
      const target = event.target as HTMLInputElement
      const length = target.value.length
      const contentWidth = calculateAutoExpandWidth(length, autoExpandMin, autoExpandMax)
      // Use the larger of initial width (captured on focus) or content width
      const width = Math.max(autoExpandMinWidth, contentWidth)
      target.style.setProperty('width', `${width}px`, 'important')
    }
    if (props.onInput) {
      props.onInput(event)
    }
  }

  const moveSymbolLeft = () => {
    // Use the memoized symbolOffset instead of recalculating
    return symbolOffset
  }

  const inputSymbol = () => {
    let symbol = '2.05rem'
    if (props.valueHeight) {
      symbol = '1.65rem'
    }
    if (props.size === 'small') {
      if (props.firstChild) {
        symbol = '1.94rem'
      } else {
        symbol = '1.95rem'
      }
    }
    return symbol
  }

  const onClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowSymbol(false)
    setInputElement(e.target)
  }

  const disableStringInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (props.type === 'number') {
      if (props.wholeNumber) {
        if (!inputNumberTypeRegex.regexPattern.test(e.key) || e.key === ',' || e.key === '.') {
          e.preventDefault()
        }
      } else {
        const _key = (e.key === ',' || e.key === '.')
        const _includeIndicator = (props.value?.toString().includes(',') || props.value?.toString().includes('.') || props.value === '')
        if (!inputNumberTypeRegex.regexPattern.test(e.key) || (_key && _includeIndicator)) {
          e.preventDefault()
        }
      }
    }
  }
  // Build CSS class for auto-expand
  const getAutoExpandClass = (): string => {
    if (!props.autoExpand) return ''
    if (props.symbol && props.symbolLeft) return 'validation-input-auto-expand validation-input-symbol-left'
    if (props.symbol) return 'validation-input-auto-expand validation-input-symbol'
    return 'validation-input-auto-expand'
  }

  return (
    <Form.Field
      width={props.fieldWidth}
      className={getAutoExpandClass()}
      style={{
        paddingLeft: props.paddingLeft,
        paddingRight: props.paddingRight,
        width: props.fieldWidth ? '' : props.fieldWidthPercentage ? props.fieldWidthPercentage : '100%',
        minWidth: props.minWidth ?? '',
      }}
    >
      {props.label ? props.label : null}

      <Popup
        className='popup warning'
        style={{ backgroundColor: 'red', borderColor: 'red' }}
        content={props.invalidMessage}
        open={(props.showWarningCase && props.showWarningId && props.showWarningCase === props.showWarningId) || (
          (props.close === undefined || !props.close) &&
          ((props.invalid !== undefined && props.invalid) ||
            (props.validatePattern !== undefined && validatedInput === false && props.value !== '')) && isFocused
        )}
        position='bottom center'
        trigger={
          <Input
            value={props.value}
            type={props.type === 'number' ? 'text' : props.type}
            placeholder={props.placeholder}
            className={props.className}
            disabled={props.disabled}
            error={props.error}
            fluid={props.fluid}
            focus={props.focus}
            inverted={props.inverted}
            loading={props.loading}
            size={props.size}
            tabIndex={props.tabIndex}
            transparent={props.transparent}
            onChange={inputOnChange}
            onClick={onClick}
            onBlur={inputOnBlur}
            onFocus={inputOnFocus}
            onInput={inputOnInput}
            icon={props.icon}
            iconPosition={props.iconPosition}
            style={{ marginTop: props.marginTop, marginBottom: props.marginBottom, zIndex: props.zIndex, width: props.width, height: props.height }}
            label={props.labelInside ? { basic: true, content: props.labelInside } : undefined}
            labelPosition={props.labelInside ? 'left' : undefined}
            onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => disableStringInput(e)}
            list={props.listId ? props.listId : undefined}
            maxLength={props.maxLength}
            readOnly={props.readOnly}
            aria-label={props['aria-label']}
            data-testid={props['data-testid']}
          />
        }
      />

      {props.symbol ? (
        <div
          style={{
            zIndex: 1,
            fontWeight: props.boldSymbol ? 'bold' : 'normal',
            display: showSymbol && props.value ? '' : 'none',
            position: 'relative',
            height: '0',
            width: !props.symbolLeft ? 'min-content' : '',
            bottom: inputSymbol(),
            left: !props.symbolLeft ? moveSymbolLeft() : '',
            right: props.symbolLeft ? moveSymbolLeft() : '',
            marginLeft: props.symbolMarginLeft,
            marginTop: props.symbolMarginTop,
            opacity: props.disabled ? 0.45 : 1,
          }}
        >
          {props.symbol}
        </div>
      ) : null}

      {props.datalist ? (
        <datalist id={props.listId}>
          {props.datalist.map((dl) => {
            return (
              <option key={dl.value} value={dl.value}>
                {dl.value}
              </option>
            )
          })}
        </datalist>
      ) : null}
    </Form.Field>
  )
}

export default ValidationInput
